import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
    Box,
    Typography,
    Radio,
    RadioGroup,
    FormControlLabel,
    Paper,
    Divider,
    Chip,
    Grid,
    TextField,
    CircularProgress
} from '@mui/material';
import { styled } from '@mui/system';
import { Package, Clock, Check, Calendar, Info } from 'lucide-react';

// Import services with the correct path
import { paymentService } from '../../../../services/paymentService';

const ShippingOption = styled(Paper)(({ theme, selected }) => ({
    padding: theme.spacing(3),
    borderRadius: theme.shape.borderRadius,
    cursor: 'pointer',
    border: `2px solid ${selected ? theme.palette.primary.main : '#e5e7eb'}`,
    backgroundColor: selected ? theme.palette.primary.light : '#fff',
    transition: 'all 0.2s ease-in-out',
    position: 'relative',
    '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 6px 12px rgba(0, 0, 0, 0.08)',
    }
}));

const OptionHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
}));

const IconWrapper = styled(Box)(({ theme, selected }) => ({
    width: 48,
    height: 48,
    borderRadius: '50%',
    backgroundColor: selected ? theme.palette.primary.main : theme.palette.grey[100],
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: theme.spacing(2),
    color: selected ? '#fff' : theme.palette.grey[700],
}));

const ShippingPrice = styled(Box)(({ theme }) => ({
    textAlign: 'center',
    marginTop: theme.spacing(2),
}));

const PriceText = styled(Typography)(({ theme }) => ({
    fontSize: '1.5rem',
    fontWeight: 700,
    color: theme.palette.text.primary,
}));

const SubscriptionInfoBox = styled(Box)(({ theme }) => ({
    padding: theme.spacing(3),
    backgroundColor: '#f0f9ff',
    borderRadius: theme.shape.borderRadius,
    borderLeft: `4px solid ${theme.palette.info.main}`,
    marginBottom: theme.spacing(4),
    position: 'relative',
}));

const InfoBox = styled(Box)(({ theme, color = 'primary.light' }) => ({
    padding: theme.spacing(3),
    backgroundColor: theme.palette[color.split('.')[0]][color.split('.')[1]],
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(4),
    textAlign: 'left',
}));

const SuccessCheck = styled(Box)(({ theme }) => ({
    width: 60,
    height: 60,
    borderRadius: '50%',
    backgroundColor: '#d1fae5',
    color: '#10b981',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 16px auto'
}));

const ShippingOptions = ({ 
    esimOptions, 
    shippingMethod, 
    setShippingMethod,
    existingSubscription 
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [subscriptionDetails, setSubscriptionDetails] = useState(null);
    const { getAccessTokenSilently } = useAuth0();
    const allEsim = esimOptions.every(option => option === 'yes');
    
    // Format a future date (days from now)
    const formatFutureDate = (days) => {
        const date = new Date();
        date.setDate(date.getDate() + days);
        return date.toISOString().split('T')[0];
    };

    // Format date to a more readable format
    const formatDate = (dateString) => {
        if (!dateString) return 'Not available';
        
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'Not available';
            
            return new Intl.DateTimeFormat('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
            }).format(date);
        } catch (e) {
            return 'Not available';
        }
    };
    
    // Fetch additional subscription details if needed - using service with better error handling
    useEffect(() => {
        if (existingSubscription && existingSubscription.id) {
            setIsLoading(true);
            
            const fetchSubscriptionDetails = async () => {
                try {
                    // Get auth token
                    const token = await getAccessTokenSilently({
                        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                        scope: 'openid profile email'
                    });
                    
                    // Try to get subscription details, handle gracefully if endpoint doesn't exist
                    try {
                        // Use payment service to get subscription status
                        const response = await paymentService.getSubscriptionStatus(
                            existingSubscription.id, 
                            token
                        );
                        
                        setSubscriptionDetails({
                            ...existingSubscription,
                            ...response.data,
                            // If next_renewal_date isn't available, try to use any other date field
                            next_renewal_date: response.data?.next_renewal_date || 
                                            existingSubscription.next_renewal_date || 
                                            existingSubscription.monthly_payment_date || 
                                            formatFutureDate(90) // Fallback to 90 days from now
                        });
                    } catch (apiError) {
                        console.warn('API endpoint may not exist:', apiError);
                        // If the API call fails, still use existing data
                        setSubscriptionDetails({
                            ...existingSubscription,
                            next_renewal_date: existingSubscription.next_renewal_date || 
                                            existingSubscription.monthly_payment_date || 
                                            formatFutureDate(90)
                        });
                    }
                } catch (error) {
                    console.error('Error in authentication:', error);
                    // Fallback to existing data if auth fails
                    setSubscriptionDetails({
                        ...existingSubscription,
                        next_renewal_date: existingSubscription.next_renewal_date || 
                                        existingSubscription.monthly_payment_date || 
                                        formatFutureDate(90)
                    });
                } finally {
                    setIsLoading(false);
                }
            };
            
            fetchSubscriptionDetails();
        }
    }, [existingSubscription, getAccessTokenSilently]);
    
    // Display for eSIM selection (simplified and aligned with physical SIM layout)
    if (allEsim) {
        return (
            <Box>
                <Typography variant="h5" gutterBottom align="center" sx={{ mb: 2 }}>
                    Choose Your Shipping
                </Typography>
                
                {/* Subscription Info Box - Same for both views */}
                {existingSubscription && (
                    <SubscriptionInfoBox>
                        {isLoading && (
                            <Box sx={{ 
                                position: 'absolute', 
                                top: 0, 
                                left: 0, 
                                right: 0, 
                                bottom: 0, 
                                bgcolor: 'rgba(255,255,255,0.7)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 2,
                                borderRadius: 'inherit'
                            }}>
                                <CircularProgress size={24} />
                            </Box>
                        )}
                        <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                            <Info size={18} style={{ marginRight: '8px' }} />
                            Your Current Subscription
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            Your transfer will continue with your current subscription:
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={4}>
                                <Typography variant="body2" color="text.secondary">Subscription type:</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {existingSubscription.subscription_type === 'annual' ? 'Annual Plan' : '3 Months Plan'}
                                </Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography variant="body2" color="text.secondary">Monthly price:</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    ${existingSubscription.subscription_type === 'annual' ? '8' : '12'}/mo
                                </Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Calendar size={14} style={{ marginRight: '4px' }} />
                                    Next renewal date:
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {subscriptionDetails ? 
                                        formatDate(subscriptionDetails.next_renewal_date) : 
                                        'Not available'}
                                </Typography>
                            </Grid>
                        </Grid>
                    </SubscriptionInfoBox>
                )}
                
                {/* eSIM selected option */}
                <ShippingOption 
                    selected={true}
                    elevation={2}
                    sx={{ mb: 4 }}
                >                    
                    <OptionHeader>
                        <IconWrapper selected={true}>
                            <Check size={24} />
                        </IconWrapper>
                        <Box>
                            <Typography variant="h6">No Physical Shipping Required</Typography>
                            <Typography variant="body2" color="text.secondary">
                                You've selected eSIM for all your lines. Your SIM cards will be delivered digitally.
                            </Typography>
                        </Box>
                    </OptionHeader>
                    
                    <Divider />
                    
                    <ShippingPrice>
                        <PriceText>$0.00</PriceText>
                    </ShippingPrice>
                </ShippingOption>
                
                <InfoBox color="primary.light">
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                        What's next?
                    </Typography>
                    <Typography variant="body2">
                        After completing your order, you'll receive an email with instructions on how to activate your eSIM.
                    </Typography>
                </InfoBox>
            </Box>
        );
    }
    
    // Display for physical SIM selection (unchanged)
    return (
        <Box>
            <Typography variant="h5" gutterBottom align="center" sx={{ mb: 2 }}>
                Choose Your Shipping
            </Typography>
            <Typography variant="body1" gutterBottom align="center" sx={{ mb: 4 }}>
                Select how you'd like to receive your physical SIM card.
            </Typography>
            
            {/* Subscription Info Box */}
            {existingSubscription && (
                <SubscriptionInfoBox>
                    {isLoading && (
                        <Box sx={{ 
                            position: 'absolute', 
                            top: 0, 
                            left: 0, 
                            right: 0, 
                            bottom: 0, 
                            bgcolor: 'rgba(255,255,255,0.7)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 2,
                            borderRadius: 'inherit'
                        }}>
                            <CircularProgress size={24} />
                        </Box>
                    )}
                    <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                        <Info size={18} style={{ marginRight: '8px' }} />
                        Your Current Subscription
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Your transfer will continue with your current subscription:
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={4}>
                            <Typography variant="body2" color="text.secondary">Subscription type:</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {existingSubscription.subscription_type === 'annual' ? 'Annual Plan' : '3 Months Plan'}
                            </Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <Typography variant="body2" color="text.secondary">Monthly price:</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                ${existingSubscription.subscription_type === 'annual' ? '8' : '12'}/mo
                            </Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                                <Calendar size={14} style={{ marginRight: '4px' }} />
                                Next renewal date:
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {subscriptionDetails ? 
                                    formatDate(subscriptionDetails.next_renewal_date) : 
                                    'Not available'}
                            </Typography>
                        </Grid>
                    </Grid>
                </SubscriptionInfoBox>
            )}
            
            <RadioGroup
                value={shippingMethod}
                onChange={(e) => setShippingMethod(e.target.value)}
            >
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <ShippingOption 
                            selected={shippingMethod === 'standard'}
                            onClick={() => setShippingMethod('standard')}
                            elevation={shippingMethod === 'standard' ? 2 : 0}
                        >
                            <FormControlLabel
                                value="standard"
                                control={<Radio />}
                                label=""
                                sx={{ 
                                    position: 'absolute',
                                    top: 16,
                                    right: 16,
                                    margin: 0,
                                }}
                            />
                            
                            <OptionHeader>
                                <IconWrapper selected={shippingMethod === 'standard'}>
                                    <Package size={24} />
                                </IconWrapper>
                                <Box>
                                    <Typography variant="h6">Standard Shipping</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Reliable delivery within 5-7 business days
                                    </Typography>
                                </Box>
                            </OptionHeader>
                            
                            <Divider />
                            
                            <ShippingPrice>
                                <PriceText>$8.00</PriceText>
                            </ShippingPrice>
                        </ShippingOption>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                        <ShippingOption 
                            selected={shippingMethod === 'expedited'}
                            onClick={() => setShippingMethod('expedited')}
                            elevation={shippingMethod === 'expedited' ? 2 : 0}
                        >
                            <FormControlLabel
                                value="expedited"
                                control={<Radio />}
                                label=""
                                sx={{ 
                                    position: 'absolute',
                                    top: 16,
                                    right: 16,
                                    margin: 0,
                                }}
                            />
                            
                            <OptionHeader>
                                <IconWrapper selected={shippingMethod === 'expedited'}>
                                    <Clock size={24} />
                                </IconWrapper>
                                <Box>
                                    <Typography variant="h6">Expedited Shipping</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Fast delivery within 2-3 business days
                                    </Typography>
                                </Box>
                            </OptionHeader>
                            
                            <Divider />
                            
                            <ShippingPrice>
                                <PriceText>$15.00</PriceText>
                            </ShippingPrice>
                        </ShippingOption>
                    </Grid>
                </Grid>
            </RadioGroup>
        </Box>
    );
};

export default ShippingOptions;